import { React, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import classNames from "classnames";

import CloseIcon from "~/content/icons/close.svg?react";
import { Footer, FooterTheme } from "./Footer/Footer";
import { useContentVisibility } from "~/hooks/useContentVisibility/useContentVisibility";

import "./MainMenuDrawer.css";

export function MainMenuDrawer({ onClose, onLogout, onShowGetHelpModal, isOpen = false }) {
  const transitionDuration = 500;
  const { t } = useTranslation();
  const [isVisible, setVisible] = useState(true);
  const { showSettings, showStudyInfo } = useContentVisibility();

  useEffect(() => {
    document.body.classList.toggle("modal-open", isOpen);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setVisible(true);
      }, transitionDuration);
      return () => clearTimeout(timer);
    }

    setVisible(false);
  }, [isOpen]);

  const showHelp = () => {
    onClose();
    onShowGetHelpModal();
  };

  const links = [
    { path: "/", label: t("home.home"), shouldShow: true },
    { path: "/study", label: t("study_info.study_info"), shouldShow: showStudyInfo },
    { path: "/settings", label: t("settings.settings"), shouldShow: showSettings },
  ];

  return (
    <div
      className={classNames({
        "main-menu-drawer": true,
        "main-menu-drawer--open": isOpen,
        "main-menu-drawer--hidden": isVisible,
      })}
      data-testid="mainMenuDrawer"
    >
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: TODO - should maybe be possible to close the drawer using the esc key */}
      <div
        className="main-menu-drawer__overlay"
        onClick={onClose}
        role="presentation"
      />
      <nav className="main-menu-drawer__content" tabIndex="-1">
        <button
          className="main-menu-drawer__close-button"
          onClick={onClose}
          aria-label={t("general.close")}
          type="button"
        >
          <CloseIcon className="main-menu-drawer__close-icon" />
        </button>
        <div className="main-menu-drawer__scroll-content">
          <ul className="main-menu-drawer__link-list">
            {links.map((link) => (
              <li
                className={classNames({
                  "main-menu-drawer__link-list-item": true,
                  "main-menu-drawer__link-list-item--hide": !link.shouldShow
                })}
                key={link.path}
              >
                <RouterLink
                  className="main-menu-drawer__link"
                  to={link.path}
                  onClick={onClose}
                >
                  {link.label}
                </RouterLink>
              </li>
            ))}
            <li className="main-menu-drawer__link-list-item">
              <button
                className="main-menu-drawer__button-link"
                onClick={showHelp}
                type="button"
              >
                {t("general.help.get_help")}
              </button>
            </li>
            <li className="main-menu-drawer__link-list-item">
              <button
                className="main-menu-drawer__button-link"
                onClick={onLogout}
                type="button"
              >
                {t("login.logout")}
              </button>
            </li>
          </ul>
        </div>
        <Footer alignText="left" theme={FooterTheme.DARK} />
      </nav>
    </div>
  );
}

MainMenuDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  onLogout: PropTypes.func.isRequired,
  onShowGetHelpModal: PropTypes.func.isRequired,
  isOpen: PropTypes.bool,
};
